import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Overlay } from 'react-bootstrap';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './DevicesList.scss';
import { ErrorMessage, FilterTag, Loader, Page, PageHeader } from 'components';
import { DevicesSortField, ErrorType, LoadState } from 'enums';
import { IFilter } from 'interfaces';
import { DevicesListItem } from './DevicesListItem';
import { DevicesListSorter } from './DevicesListSorter';
import { FiltersPopover } from './FiltersPopover';
import { DeviceInfoService, DevicesService } from 'services';
import { useCancelablePromise, useLoadMore } from 'hooks';

export function DevicesList() {
    const { t, i18n } = useTranslation();
    const devices = DevicesService.useDevices();
    let devicesLoadState = DevicesService.useDevicesState();
    const filters = DevicesService.useFilters();
    const { sortAscending, sortField } = DevicesService.useSorting();
    const { cancelablePromise } = useCancelablePromise();

    const [infoLoadState, setInfoLoadState] = useState<LoadState>(LoadState.Loading);
    const [infoError, setInfoError] = useState('');
    const [filtersOpen, setFiltersOpen] = useState(false);
    const filtersButtonRef = useRef(null);
    // We clear and reload the devices list, so don't render it intially to prevent a flash of old data.
    const [hasReloadedDevices, setHasReloadedDevices] = useState(false);

    // "loadState" is a combination of loading the devices and the manufacturer info.
    let loadState = LoadState.Loading;

    if (devicesLoadState === LoadState.Error || infoLoadState === LoadState.Error) {
        loadState = LoadState.Error;
    } else if (devicesLoadState === LoadState.Success && infoLoadState === LoadState.Success) {
        loadState = LoadState.Success;
    }

    const canLoadMore = loadState === LoadState.Success && DevicesService.canLoadMore;
    const checkLoadMore = useLoadMore(canLoadMore, () => {
        DevicesService.loadMoreDevices();
    });

    // Calling checkLoadMore every render to solve the problem of if the data doesn't
    // fill the page, there's no scrollbar to trigger the scroll events to load more.
    checkLoadMore();

    // Load devices the first time we render
    useEffect(() => {
        setHasReloadedDevices(true);
        cancelablePromise(DeviceInfoService.getManufacturerMap())
            .then(() => {
                setInfoLoadState(LoadState.Success);
            })
            .catch((e) => {
                setInfoLoadState(LoadState.Error);
                setInfoError(e + '')
            });
    }, []);

    const toggleSortField = (field: DevicesSortField) => {
        DevicesService.toggleSortField(field, devices);
    }

    const tableHeaders = (
        <div className="devicesHeaders">
            <div className="devicesHeader devicesHeaders_addNew">
                <Link to="/devices/new" title={t('devicesList.headers.addNewTooltip')} data-cy="devices_addNewLink">
                    {t('devicesList.headers.addNew')}
                    <FontAwesomeIcon icon="plus" className="devicesHeaders_addNewIcon" />
                </Link>
            </div>
            <DevicesListSorter
                className="devicesHeader devicesHeaders_tests"
                activeField={sortField}
                ascending={sortAscending}
                field={DevicesSortField.PassingTests}
                onToggle={toggleSortField}
            >
                {t('devicesList.headers.passingTests')}
            </DevicesListSorter>
            <DevicesListSorter
                className="devicesHeader devicesHeaders_lastRun"
                activeField={sortField}
                ascending={sortAscending}
                field={DevicesSortField.LastRunAt}
                onToggle={toggleSortField}
            >
                {t('devicesList.headers.lastRun')}
            </DevicesListSorter>
            <DevicesListSorter
                className="devicesHeader devicesHeaders_addedDate"
                activeField={sortField}
                ascending={sortAscending}
                field={DevicesSortField.CreatedAt}
                onToggle={toggleSortField}
            >
                {t('devicesList.headers.addedDate')}
            </DevicesListSorter>
        </div>
    );

    let content: ReactNode;
    switch (loadState) {
        case LoadState.Loading:
        case LoadState.Success:
            if (devices.length > 0 || loadState === LoadState.Loading) {
                content = (<>
                    {hasReloadedDevices && (
                        <ul className="devicesList" data-cy="devices_deviceList">
                            {devices.map(device => (
                                <DevicesListItem device={device} key={device.id} />
                            ))}
                        </ul>
                    )}
                    {loadState === LoadState.Loading && (
                        <Loader />
                    )}
                </>);
            } else {
                if (DevicesService.filters.length === 0) {
                    content = (
                        <div className="devicesList_emptyAddNew">
                            <div className="devicesList_addNewArrow">
                                <FontAwesomeIcon icon="reply" />
                            </div>
                            {t('devicesList.emptyMessageAddNew')}
                        </div>
                    );
                } else {
                    content = (
                        <div className="devicesList_emptyFiltering">
                            {t('devicesList.emptyMessageFiltering')}
                        </div>
                    );
                }
            }
            break;
        case LoadState.Error:
            content = (
                <ErrorMessage type={ErrorType.Generic} message={infoError || DevicesService.loadErrorMessage} />
            );
            break;
    }

    const onAddedFilter = () => {
        DevicesService.getDevices();
    }

    const removeFilter = (filter: IFilter) => {
        DevicesService.removeFilter(filter);
        DevicesService.getDevices();
    }

    let filtersHeader: ReactNode;
    if (infoLoadState === LoadState.Success) {
        filtersHeader = (
            <div className="devicesList_filters">
                <ul className="devicesList_filterTags">
                    {filters.map(filter => (
                        <FilterTag
                            filter={filter}
                            onRemove={() => removeFilter(filter)}
                            key={filter.id}
                        />
                    ))}
                </ul>
                <button
                    type="button"
                    className="devicesList_filtersButton"
                    onClick={() => setFiltersOpen(!filtersOpen)}
                    ref={filtersButtonRef}
                >
                    {t('devicesList.filters.dropdown')}
                    <FontAwesomeIcon icon="caret-down" className="devicesList_filtersCaret" />
                </button>
                <Overlay
                    target={filtersButtonRef}
                    show={filtersOpen}
                    onHide={() => setFiltersOpen(false)}
                    rootClose
                    placement="bottom"
                >
                    <FiltersPopover id="deviceFilters_popover" onAddFilters={onAddedFilter} />
                </Overlay>
            </div>
        );
    }

    return (
        <Page>
            <PageHeader 
                title={t('devicesList.title')}
                right={filtersHeader}
            />
            {tableHeaders}
            {content}
        </Page>
    );
}